import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMarketingHeadOwnership } from '../../pages/marketingRoutes';
import '../v2/landing.css';
import FooterV2 from '../v2/FooterV2';
import ConnectHero from './ConnectHero';
import { StillContext, TeacherMotionContext, useMedia } from './sections/useReveal';
import {
  AskBuffy, AssessmentsSection, AssignTrackSection, ClosingCta, CourseSection, FaqSection, FreeSteps, StudentViewSection,
} from './sections';
import './connect.css';

/* Teacher landing v2, "Connecting the SAT to your classroom" (2026-09-29).
   Localhost-only preview at /teachers-preview (guarded in App.js).
   PAGE STRUCTURE v3 (Sandy, 2026-09-29): the hero (ConnectHero, owned
   separately); "Free for teachers. Here is how you start." (3 steps); the
   feature sections (FeatureHeader): F1 Any curriculum, F2 Assessments,
   F3 Ask Buffy, F4 Assign practice and track progress, then "What
   students see" (the student side of F4's practice); the FAQ; the
   closing banner; the footer.
   ?settled=1 renders every section in its final frame (audit shots).
   A #hash on a fresh load lands on its heading (final formatting #24: the
   browser applies the hash before React renders the page).
   .marketing-type (landing.css) gives DM Sans and the Bootstrap
   spacing-collision fixes. */
export default function ConnectLanding() {
  // Remove the static shell's description/canonical while this page owns them (2026-10-28).
  useMarketingHeadOwnership();
  const [paused, setPaused] = useState(false);
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return undefined;
    // two frames: the sections have rendered and laid out
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ block: 'start' });
      });
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <TeacherMotionContext.Provider value={{ paused, setPaused }}>
      <div id="teacher-marketing-page" className="marketing-type bg-white cx-teacher-page" data-motion={paused || reduced ? 'paused' : 'on'}>
        {/* the teacher page's own title and share text; "/" and /teachers
            both show this page, and the root is the one to index (2026-11-03) */}
        <Helmet>
          <title>Brain Buffs: Connecting the SAT to your classroom</title>
          <meta name="description" content="Choose your curriculum and see where the SAT shows up. See where your students struggle on SAT Math and Reading and Writing, and why. Free for teachers." />
          <meta property="og:title" content="Brain Buffs: Connecting the SAT to your classroom" />
          <meta property="og:description" content="Choose your curriculum and see where the SAT shows up. Free for teachers." />
          <link rel="canonical" href="https://www.brainbuffs.com/" />
        </Helmet>
        <ConnectHero />
        <StillContext.Provider value={paused || reduced}>
          <FreeSteps />
          <CourseSection />
          <AssessmentsSection />
          <AskBuffy />
          <AssignTrackSection />
          <StudentViewSection />
          <FaqSection />
          <ClosingCta />
        </StillContext.Provider>
        {/* Same 1200px content column as the sections (1232 box incl. 16px md padding):
            text edges 120/1320 at 1440, 32/992 at 1024, 16/359 at 375. */}
        <FooterV2 innerClassName="max-w-[1232px] md:px-4 text-left" />
      </div>
    </TeacherMotionContext.Provider>
  );
}
